import Box from 'carbon-react/lib/components/box';
import Button from 'carbon-react/lib/components/button/__next__';
import Card, {
  CardColumn,
  CardFooter,
  CardRow,
} from 'carbon-react/lib/components/card';
import Divider from 'carbon-react/lib/components/divider';
import Form from 'carbon-react/lib/components/form';
import Loader from 'carbon-react/lib/components/loader/__next__';
import Message from 'carbon-react/lib/components/message';
import Password from 'carbon-react/lib/components/password';
import Textbox from 'carbon-react/lib/components/textbox';
import Typography from 'carbon-react/lib/components/typography';
import { SubmitEventHandler, useState } from 'react';
import { send } from '../../../helpers/requests';
import toastr from '../../../helpers/toastr';

export interface UpdateWSUFormProps {
  intacctCompanyId: string;
}

export function UpdateWSUForm({ intacctCompanyId }: UpdateWSUFormProps) {
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [userId, setUserId] = useState('');
  const [inputErrors, setInputErrors] = useState<{
    password?: boolean;
    userId?: boolean;
  }>({});

  const submit = () => {
    if (loading) return;
    if (!password || !userId) return;

    void (async () => {
      setLoading(true);
      setError(undefined);
      try {
        const data = {
          companyId: intacctCompanyId,
          password,
          userId,
        };
        await send({
          url: '/admin/update-wsu',
          method: 'POST',
          data,
          credentials: 'include',
          dontCheckForUnauthorized: true,
          useStream: true,
        });
        toastr.success('Credentials updated successfully');
      } catch (error_: unknown) {
        setError(
          error_ instanceof Error
            ? error_.message
            : 'Failed to update credentials',
        );
      } finally {
        setLoading(false);
      }
    })();
  };

  const handleSubmit: SubmitEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    submit();
  };

  const validateInput = (key: keyof typeof inputErrors, value: string) => {
    const errs = { ...inputErrors };
    if (!value) {
      errs[key] = true;
    } else {
      delete errs[key];
    }
    setInputErrors(errs);
  };

  const errorCount = Object.keys(inputErrors).length;

  return (
    <>
      <Card
        footer={
          <CardFooter px={1} py={1}>
            <Box
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              width="100%"
              gap={2}
            >
              <Button
                disabled={errorCount > 0}
                key="verify"
                onClick={submit}
                type="button"
              >
                {loading ? (
                  <Loader
                    loaderType="ring"
                    variant="inline"
                    size="small"
                    loaderLabel="Updating..."
                  />
                ) : (
                  'Update WSU credentials'
                )}
              </Button>
            </Box>
          </CardFooter>
        }
      >
        <CardRow pb={0}>
          <CardColumn align="left">
            <Typography variant="h1" mt={2}>
              Web Services User credentials
            </Typography>
          </CardColumn>
        </CardRow>
        <Divider type="horizontal" mt={2} mb={2} />
        <Form errorCount={errorCount} onSubmit={handleSubmit}>
          <div className="inputs">
            <Textbox
              required
              error={inputErrors.userId ? 'This field is required' : undefined}
              label="Web Services User ID"
              labelInline={false}
              name="userId"
              onBlur={() => validateInput('userId', userId)}
              onChange={(e) => setUserId(e.target.value)}
              // @ts-expect-error -- type not on textbox
              type="text"
              value={userId}
            />
            <Password
              required
              error={
                inputErrors.password ? 'This field is required' : undefined
              }
              label="Web Services User password"
              labelInline={false}
              name="password"
              onBlur={() => validateInput('password', password)}
              onChange={(e) => setPassword(e.target.value)}
              value={password}
            />
          </div>
        </Form>
      </Card>
      {!!error && (
        <Box mt="10px">
          <Message variant="error">{error}</Message>
        </Box>
      )}
    </>
  );
}
